import { useState } from "react";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { authApi } from "@/lib/auth-api";

interface SessionExpiredModalProps {
	open: boolean;
	onReauthenticated: () => void;
}

export function SessionExpiredModal({ open, onReauthenticated }: SessionExpiredModalProps) {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			await authApi.login({ email, password });
			setEmail("");
			setPassword("");
			onReauthenticated();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Blad logowania");
		} finally {
			setLoading(false);
		}
	};

	const handleGoogleLogin = () => {
		window.location.href = authApi.googleAuthorize();
	};

	return (
		<Dialog open={open}>
			<DialogContent
				className="max-w-sm"
				onPointerDownOutside={(e) => e.preventDefault()}
				onEscapeKeyDown={(e) => e.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle>Sesja wygasla</DialogTitle>
					<DialogDescription>
						Zaloguj sie ponownie aby kontynuowac prace. Twoje dane nie zostana utracone.
					</DialogDescription>
				</DialogHeader>

				{/* DialogBody: na telefonie (arkusz od dolu) tresc przewija sie nad klawiatura.
				    Odstepy jak dawny kontener (pt-0 pb-6), wiec desktop sie nie zmienia. */}
				<DialogBody className="pt-0 pb-6 space-y-4">
					<button
						type="button"
						onClick={handleGoogleLogin}
						className="flex w-full items-center justify-center gap-3 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted max-md:h-11"
					>
						<svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
							<path
								d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
								fill="#4285F4"
							/>
							<path
								d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
								fill="#34A853"
							/>
							<path
								d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
								fill="#FBBC05"
							/>
							<path
								d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
								fill="#EA4335"
							/>
						</svg>
						Zaloguj sie przez Google
					</button>

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-border" />
						</div>
						<div className="relative flex justify-center text-xs">
							<span className="bg-card px-2 text-muted-foreground">lub</span>
						</div>
					</div>

					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<label
								htmlFor="reauth-email"
								className="mb-1.5 block text-sm font-medium text-foreground"
							>
								Email
							</label>
							<input
								id="reauth-email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								autoComplete="email"
								className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary max-md:h-11 max-md:text-base"
								placeholder="jan@apteka.pl"
							/>
						</div>
						<div>
							<label
								htmlFor="reauth-password"
								className="mb-1.5 block text-sm font-medium text-foreground"
							>
								Haslo
							</label>
							<input
								id="reauth-password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								autoComplete="current-password"
								className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary max-md:h-11 max-md:text-base"
							/>
						</div>

						{error && <p className="text-sm text-destructive">{error}</p>}

						<button
							type="submit"
							disabled={loading}
							className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 max-md:h-11"
						>
							{loading ? "Logowanie..." : "Zaloguj sie"}
						</button>
					</form>
				</DialogBody>
			</DialogContent>
		</Dialog>
	);
}
